import { useState } from "react";
import { Box, Paper, Typography, Button, Chip } from "@mui/material";
import type { ICommentType } from "../tabs/MemoriesTab";

const TRUNCATE_LENGTH = 300;

interface CommentCardItemProps {
  comment: ICommentType;
  isAdmin: boolean;
  handleApprove: (id: string, approve: boolean) => void;
  handleDelete: (id: string) => void;
}

const CARD_COLORS = [
  "#ffffff",
  "#fdf6ee",
  "#f0f7ff",
  "#f3faf3",
  "#fdf3f8",
  "#f5f0ff",
];

function cardColor(id: string) {
  const n = id.split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return CARD_COLORS[n % CARD_COLORS.length];
}

export default function CommentCardItem({
  comment,
  isAdmin,
  handleApprove,
  handleDelete,
}: CommentCardItemProps) {
  const [expanded, setExpanded] = useState(false);

  const isLong = comment.content.length > TRUNCATE_LENGTH;
  const displayContent =
    !expanded && isLong
      ? comment.content.slice(0, TRUNCATE_LENGTH)
      : comment.content;

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: cardColor(comment.id),
        borderRadius: "18px",
        p: 3,
        border: "1px solid",
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        minHeight: 180,
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.1)" },
      }}
    >
      {/* Pending badge */}
      {isAdmin && !comment.is_approved && (
        <Chip
          label="Pending"
          size="small"
          color="warning"
          sx={{ alignSelf: "flex-start", fontSize: "0.65rem", height: 20, mb: 1 }}
        />
      )}

      {/* Quote decoration */}
      <Typography
        sx={{
          fontSize: "3.5rem",
          lineHeight: 0.6,
          color: "rgba(0,0,0,0.1)",
          fontFamily: "Georgia, serif",
          userSelect: "none",
        }}
      >
        &quot;
      </Typography>

      {/* Content */}
      <Typography
        sx={{
          fontSize: "0.92rem",
          lineHeight: 1.65,
          color: "#2d2d2d",
          mt: 1.5,
          flex: 1,
          wordBreak: "break-word",
        }}
      >
        {displayContent}
        {isLong && !expanded && (
          <Box
            component="span"
            onClick={() => setExpanded(true)}
            sx={{ color: "primary.main", fontWeight: 600, cursor: "pointer", ml: 0.5 }}
          >
            … read more
          </Box>
        )}
        {isLong && expanded && (
          <Box
            component="span"
            onClick={() => setExpanded(false)}
            sx={{ color: "primary.main", fontWeight: 600, cursor: "pointer", ml: 0.5 }}
          >
            {" "}read less
          </Box>
        )}
      </Typography>

      {/* Author */}
      <Typography
        sx={{
          mt: 2,
          fontSize: "0.8rem",
          color: "text.secondary",
          fontStyle: "italic",
          textAlign: "right",
        }}
      >
        From {comment.author || "Anonymous"}
      </Typography>

      {/* Admin actions — always visible */}
      {isAdmin && (
        <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
          <Button
            size="small"
            onClick={() => handleApprove(comment.id, !!comment.is_approved)}
            sx={{
              bgcolor: comment.is_approved ? "#dcfce7" : "#16a34a",
              color: comment.is_approved ? "#16a34a" : "#fff",
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "11px",
              px: 1.5,
              py: 0.4,
              minWidth: "unset",
              "&:hover": { bgcolor: "#15803d", color: "#fff" },
            }}
          >
            {comment.is_approved ? "Unapprove" : "Approve"}
          </Button>
          <Button
            size="small"
            onClick={() => handleDelete(comment.id)}
            sx={{
              bgcolor: "#fef2f2",
              color: "#ef4444",
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "11px",
              px: 1.5,
              py: 0.4,
              minWidth: "unset",
              "&:hover": { bgcolor: "#ef4444", color: "#fff" },
            }}
          >
            Delete
          </Button>
        </Box>
      )}
    </Paper>
  );
}
